import { RemoteConfigService } from '@web_opxp/features';
import ENV from '../base/env';
import { CurrencyDisplayOption, EProductTypes, IPrice, IProductData, ISubscriptionPeriod } from './types';

const defaultSubscribeConfig = { variant: 'fallback', products: [100, 101, 102], ob_flow: false };
type TSubscribeConfig = typeof defaultSubscribeConfig;
let subscribeConfig: TSubscribeConfig | undefined;
export function getSubscribeConfig() {
  if (!subscribeConfig) {
    subscribeConfig = RemoteConfigService.getObjectConfigValue<TSubscribeConfig>(
      ENV.RemoteConfigSubscribe,
      defaultSubscribeConfig
    );
  }
  return subscribeConfig;
}
export const getProductsConfig = () => getSubscribeConfig()?.products;
// Don't change the following to ?? because variant can be empty string
export const getProductsVariantConfig = () => getSubscribeConfig()?.variant || 'fallback';
export const getProductsObFlowConfig = () => getSubscribeConfig()?.ob_flow;

// Lowest price amount goes first
const priceComparator = (priceA: IPrice | undefined, priceB: IPrice | undefined) => {
  if (priceA === undefined || priceB === undefined) return 0;
  if (priceA.currency === priceB.currency) {
    return priceA.amount - priceB.amount;
  }
  return 0;
};

const durationMapper = {
  day: 30,
  month: 20,
  year: 10,
};

const durationComparator = (durationA: ISubscriptionPeriod | undefined, durationB: ISubscriptionPeriod | undefined) => {
  if (durationA === undefined || durationB === undefined) return 0;
  // if durationTypes are differ, then sorts like day -> month -> year, assuming that year cost is cheapest.
  if (durationA.periodType !== durationB.periodType) {
    return durationMapper[durationB.periodType] - durationMapper[durationA.periodType];
  }
  // if durationTypes are equal, then sort by amount of duration, lowest goes first
  return durationB.periodValue - durationA.periodValue;
};

export const sortProducts = (productA: IProductData, productB: IProductData): number => {
  // Product of type subscription(3) should go first
  if (productA.type === EProductTypes.ONE_TIME_PAYMENT) {
    if (productB.type === EProductTypes.SUBSCRIPTION) {
      return 1;
    }
    // if both products are type of 1, then first goes lowest discounted price
    return priceComparator(productA.discountedPrice, productB.discountedPrice);
  } else if (productA.type === EProductTypes.SUBSCRIPTION) {
    if (productB.type === EProductTypes.ONE_TIME_PAYMENT) {
      return -1;
    }
    // if both products are type of 3, then compare by subscription duration
    const durationResult = durationComparator(productA.subscriptionDetails, productB.subscriptionDetails);
    // if durationComparator returns 0, ie durations are same, then compare by price
    if (durationResult === 0) {
      return priceComparator(productA.discountedPrice, productB.discountedPrice);
    }
    return durationResult;
  }
  return 0;
};

export function formatCurrency(
  price: number,
  currency: string,
  localeAlias = 'de',
  display: CurrencyDisplayOption = 'symbol'
) {
  try {
    // try to use Intl API
    return new Intl.NumberFormat(localeAlias, { style: 'currency', currency, currencyDisplay: display }).format(price);
  } catch (error) {
    // fallback to default formatting for older browsers
    const priceStr = Number(price).toFixed(2);
    return `${priceStr}\u00a0${currency}`;
  }
}

export function calculateProductDisplayPrice(data: IProductData, price: string) {
  if (isProductOneTimePayment(data) || data.subscriptionDetails == null) {
    return Math.round(parseFloat(price) * 100) / 100;
  }

  const periodValue = data.subscriptionDetails.periodValue;
  const periodType = data.subscriptionDetails.periodType;
  const period = periodValue * (periodType === 'year' ? 12 : 1);

  return Math.round((parseFloat(price) * 100) / period) / 100;
}

export function getProductId(data: IProductData) {
  return data.id;
}

export function getProductName(data: IProductData) {
  return data.name;
}

export function getProductTranslatedTitle(data: IProductData, t: (id: string, fallback: string) => string) {
  const translated = t('Products.Title.' + getProductId(data), getProductName(data));
  return translated === ' ' ? 'Product' : translated;
}

export function getProductPrice(data: IProductData) {
  return calculateProductDisplayPrice(data, data.normalPrice ? data.normalPrice.amount.toString() : '0');
}

export function getProductDiscountedPrice(data: IProductData) {
  return calculateProductDisplayPrice(data, data.discountedPrice ? data.discountedPrice.amount.toString() : '0');
}

export function getProductCurrency(data: IProductData) {
  return data.normalPrice ? data.normalPrice.currency : '';
}

export function isProductRecommended(data: IProductData) {
  return data.recommended;
}

export function isProductDiscounted(data: IProductData) {
  return getProductPrice(data) !== getProductDiscountedPrice(data);
}

export function isProductOneTimePayment(data: IProductData) {
  return data.type === EProductTypes.ONE_TIME_PAYMENT;
}

export function isProductBundle(data: IProductData) {
  return data.type === EProductTypes.BUNDLE;
}

export function isProductSubscription(data: IProductData) {
  return data.type === EProductTypes.SUBSCRIPTION;
}

export function hasProductFreeTrial(data: IProductData) {
  return getProductFreeTrial(data) > 0;
}

export function getProductFreeTrial(data: IProductData) {
  if (!data.subscriptionDetails || !data.subscriptionDetails.trialPeriodDays) {
    return 0;
  }
  return data.subscriptionDetails.trialPeriodDays;
}

export function getDiscountPercentage(data: IProductData) {
  return data.coupon?.discountPercentage;
}

export function getProductPrefix(data: IProductData) {
  return data.prefix;
}

const FALLBACK_COUNTRY = 'GLOBAL';

export function getProductCountry(data: IProductData) {
  // BW-6871: group package always have GLOBAL country
  const isGroup = isGroupPackage(data);
  return isGroup || !data.country ? FALLBACK_COUNTRY : data.country;
}

export function getCountryFromProductList(products: IProductData[]) {
  return products.length > 0 ? products[0].country : undefined;
}

export function getProductPeriod(data: IProductData) {
  const subscription = data.subscriptionDetails;
  if (!subscription) {
    return 'LT'; // lifetime alias for one-time payments
  }
  const { periodType, periodValue } = subscription;
  const numValue = periodValue;
  // pluralize period type
  const typeValue: string = periodValue > 1 ? `${periodType}s` : periodType;
  return `${numValue}-${typeValue}`;
}

export function getProductTrialSuffix(data: IProductData) {
  const subscription = data.subscriptionDetails;
  if (!subscription) {
    return '';
  }
  if (!subscription.trialPeriodDays) {
    return '';
  }
  return `-${subscription.trialPeriodDays}-Free-trial`;
}

function isGroupPackage(data: IProductData) {
  const { type, maxGroupUsers } = data;
  return !!maxGroupUsers && (type === EProductTypes.GROUP_ONE_TIME || type === EProductTypes.GROUP_SUBSCRIPTION);
}

export function getGroupSuffix(data: IProductData) {
  const isGroup = isGroupPackage(data);
  if (!isGroup) {
    return '';
  }
  const { maxGroupUsers } = data;
  return `-GROUP-${maxGroupUsers}`;
}

export function getProductPlanId(data: IProductData) {
  // Don't change the following to ?? because value can be empty string
  const prefix = getProductPrefix(data) || 'NN';
  const country = getProductCountry(data);
  const period = getProductPeriod(data);
  const trialSuffix = getProductTrialSuffix(data);
  const groupSuffix = getGroupSuffix(data);
  const currency = getProductCurrency(data) || 'EUR';
  return `${prefix}-${country}-${period}${trialSuffix}${groupSuffix}-${currency}`;
}
